<template>
  <div>
    <v-overlay :value="pageLoading" absolute>
      <v-progress-circular indeterminate size="64"/>
    </v-overlay>

    <!-- 篩選 + 表格合併為同一區塊 -->
    <v-card elevation="1" class="table-card">
      <!-- 篩選標題列 -->
      <div
        class="filter-header d-flex align-center py-2 px-4"
        style="cursor: pointer;"
        @click="filterPanelExpanded = !filterPanelExpanded"
      >
        <v-icon small color="grey darken-1" class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-body-2 font-weight-medium grey--text text--darken-2">篩選條件</span>
        <v-chip
          v-if="activeFilterCount > 0"
          x-small
          color="primary"
          text-color="white"
          class="ml-2"
        >
          {{ activeFilterCount }}
        </v-chip>
        <v-spacer />
        <v-icon small color="grey">
          {{ filterPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </div>

      <!-- 可收合的篩選內容 -->
      <v-expand-transition>
        <div v-show="filterPanelExpanded">
          <v-divider />
          <div class="filter-content px-4 pt-3 pb-2 d-flex align-center flex-wrap" style="gap: 12px;">
            <!-- 姓名/手機/生日 -->
            <v-text-field
              v-model="filters.searchText"
              label="姓名/手機/生日"
              placeholder="輸入關鍵字"
              hide-details
              clearable
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              style="max-width: 220px; min-width: 180px;"
            />
            <!-- 診所登入：選醫師 / 醫師登入：選診所 -->
            <v-select
              v-if="clinicUser"
              v-model="filters.doctorCode"
              :items="doctorList"
              item-text="doctor_name"
              item-value="doctor_code"
              label="預約醫師"
              hide-details
              clearable
              outlined
              dense
              prepend-inner-icon="mdi-doctor"
              style="max-width: 200px; min-width: 160px;"
            />
            <v-select
              v-else
              v-model="filters.clinicCode"
              :items="clinicList"
              item-text="clinic_name"
              item-value="clinic_code"
              label="預約診所"
              hide-details
              clearable
              outlined
              dense
              prepend-inner-icon="mdi-hospital-building"
              style="max-width: 200px; min-width: 160px;"
            />
            <!-- 建立日期 -->
            <v-menu
              ref="createdDateMenu"
              v-model="createdDateMenuOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="createdDateRangeText"
                  label="建立日期"
                  readonly
                  clearable
                  hide-details
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-clock"
                  v-bind="attrs"
                  v-on="on"
                  style="max-width: 240px; min-width: 200px;"
                  @click:clear="filters.createdDateRange = []"
                />
              </template>
              <v-date-picker
                v-model="filters.createdDateRange"
                no-title
                scrollable
                range
                locale="zh"
              >
                <v-spacer />
                <v-btn text color="grey" @click="createdDateMenuOpen = false">取消</v-btn>
                <v-btn text color="primary" @click="createdDateMenuOpen = false">確認</v-btn>
              </v-date-picker>
            </v-menu>
            <!-- 偏好日期 -->
            <v-menu
              ref="bookDateMenu"
              v-model="bookDateMenuOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="bookDateRangeText"
                  label="偏好日期"
                  readonly
                  clearable
                  hide-details
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-range"
                  v-bind="attrs"
                  v-on="on"
                  style="max-width: 240px; min-width: 200px;"
                  @click:clear="filters.bookDateRange = []"
                />
              </template>
              <v-date-picker
                v-model="filters.bookDateRange"
                no-title
                scrollable
                range
                locale="zh"
              >
                <v-spacer />
                <v-btn text color="grey" @click="bookDateMenuOpen = false">取消</v-btn>
                <v-btn text color="primary" @click="bookDateMenuOpen = false">確認</v-btn>
              </v-date-picker>
            </v-menu>
            <!-- 處理結果：僅在已聯繫分頁顯示 -->
            <v-select
              v-if="status === 1"
              v-model="filters.completedList"
              :items="completedOptions"
              label="處理結果"
              multiple
              small-chips
              deletable-chips
              hide-details
              clearable
              outlined
              dense
              style="max-width: 220px; min-width: 180px;"
            />
            <!-- 操作按鈕 -->
            <v-btn
              color="primary"
              depressed
              small
              @click="applyFilter"
            >
              <v-icon left small>mdi-magnify</v-icon>
              套用篩選
            </v-btn>
            <v-btn
              outlined
              small
              color="grey darken-1"
              @click="clearFilter"
            >
              <v-icon left small>mdi-filter-remove</v-icon>
              清除篩選
            </v-btn>
          </div>
        </div>
      </v-expand-transition>

      <v-divider />

      <v-data-table
        :headers="headers"
        :items="appointments"
        :page.sync="page"
        :server-items-length="totalAppointments"
        fixed-header
        :height="tableHeight"
        :items-per-page.sync="limit"
        item-key="id"
        :loading="pageLoading"
        :footer-props="{itemsPerPageOptions: [10, 20, 50]}"
        :no-data-text="status? $t('ReserveTable.noContact') : $t('ReserveTable.noUnContact')"
        :item-class="setCancelRed"
      >
      <template v-slot:item.birthday="{ item }">
        <template v-if="item.birthday">{{ parseInt(item.birthday) * 1000 | formatROCDate }}</template>
      </template>
      <template v-slot:item.prefer_time="{ item }">
        {{ showPreferTime(item) }}
      </template>
      <template v-slot:item.complete_status="{ item }">
        <div v-if="item.complete_status">{{ item.complete_status }}</div>
        <div v-if="Number(item.completed) === 4">病患自行取消</div>
        <div v-if="item.insert_system_last_time">(點擊自動寫入約診時間：{{ item.insert_system_last_time }})</div>
      </template>
      <template v-slot:item.crm_note="{ item }">
        <span>
          {{ (item.crm_note && item.crm_note.length > 20) ? item.crm_note.substr(0, 20) + '...' : item.crm_note }}
        </span>
        <v-btn icon color="primary" @click="editCrmNote(item)">
          <v-icon dark x-small>
            mdi-pencil
          </v-icon>
        </v-btn>
      </template>
      <template v-slot:item.action="{ item }">
        <template v-if="Number(item.appointment_status) === 0">
          <template v-if="Number(item.completed) === 4">
            病患要求取消
            <v-btn outlined small color="primary" style="margin: 5px 0;" @click="handleChangeStatus(item, 'know')">
              知道了，移動到已聯繫
            </v-btn>
          </template>
          <template v-else-if="item.book_time">
            <v-btn outlined small color="primary" style="margin: 5px 0;" @click="handleChangeStatus(item, 'allow')">
              {{ $t('ReserveTable.canAppointment') }}
            </v-btn>
            <br/>
            <v-btn outlined small color="primary" style="margin: 5px 0;" @click="handleChangeStatus(item, 'reject')">
              {{ $t('ReserveTable.canNotAppointment') }}
            </v-btn>
            <br/>
            <v-btn outlined small color="primary" style="margin: 5px 0;" @click="handleChangeStatus(item, 'extra')">
              {{ $t('ReserveTable.additionalArrange') }}
            </v-btn>
          </template>
          <v-btn v-else outlined small color="primary" style="margin: 5px 0;"
                 @click="handleChangeStatus(item, 'concat')">
            {{ $t('ReserveTable.completeConcat') }}
          </v-btn>
        </template>
        <v-btn v-else outlined small color="primary" style="margin: 5px 0;"
               @click="editAppointment = item; changeStatus()">
          {{ $t('ReserveTable.withdraw') }}
        </v-btn>
      </template>
      <template v-slot:item.send="{ item }">
        <v-menu offset-x left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              small
              v-bind="attrs"
              @click.prevent="editAppointment = item; getSendChannelInfo($event, on)"
            >
              傳送訊息
            </v-btn>
          </template>
          <v-list dense outlined class="text-body-2">
            <v-list-item
              class="justify-center"
              @click="$refs.SendLineMessageV2.sendMessageDialog = true"
            >
              LINE訊息{{ sendLineSuffix }}
            </v-list-item>
            <v-list-item
              class="justify-center"
              @click="$refs.smsMessageV2.sendMessageDialog = true"
            >
              簡訊{{ sendSmsSuffix }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    </v-card>

    <v-dialog v-if="crmNoteDialog" max-width="500" v-model="crmNoteDialog" persistent>
      <v-card>
        <v-card-title>{{ $t('ReserveTable.editCrmNote') }}</v-card-title>
        <v-card-text>
          <v-textarea v-model="editAppointment.crm_note" hide-details outlined no-resize/>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" text @click="update">
            {{ $t('ReserveTable.ok') }}
          </v-btn>
          <v-btn text @click="crmNoteDialog=false">
            {{ $t('GENERAL.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="confirmDialog" max-width="500" v-model="confirmDialog">
      <v-card class="px-8 pt-5 pb-6">
        <v-card-title>{{ confirmTitle }}</v-card-title>
        <v-card-text v-html="confirmText"/>
        <v-card-actions class="justify-space-around">
          <v-btn color="primary" @click="changeStatus">{{ $t('ReserveTable.confirm') }}</v-btn>
          <template v-if="showInsertSystemBtn(editAppointment) & actionType === 'allow'">
            <v-btn
              color="blue-6"
              dark
              @click="handleChangeStatus(editAppointment, 'allowInsert')"
            >
              {{ editAppointment.system_code === 'manual' ? '點擊寫入約診表' : '點擊寫入健保系統' }}
            </v-btn>
          </template>
          <v-btn v-if="rejectItem" class="white--text" color="blue-6" @click="rejectReasonDialog = true">{{
              $t('ReserveTable.otherReason')
            }}
          </v-btn>
          <v-btn color="grey-5" class="white--text" @click="confirmDialog = false">{{ $t('GENERAL.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="messageDialog" max-width="500" v-model="messageDialog">
      <v-card>
        <v-card-title class="headline justify-center" v-text="messageTitle"/>
        <v-card-title v-if="messageText" class="headline justify-center">{{ messageText }}</v-card-title>
        <v-card-actions class="justify-center">
          <v-btn color="primary" @click="messageDialog = false" v-text="$t('ReserveTable.know')"/>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="rejectReasonDialog" :max-width="$vuetify.breakpoint.mdAndUp ? 650 : 500"
              v-model="rejectReasonDialog">
      <v-card>
        <v-card-title>{{ $t('ReserveTable.reasonTitle') }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col v-for="(reject, i) in rejectItem.remind_3" :key="i" cols="12" sm="6" md="3" class="text-center py-2">
              <v-btn block text color="blue" class="text-subtitle-1"
                     @click="handleChangeStatus(rejectItem, 'reject', i)">
                {{ reject.reason }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-if="insertFailDialog" max-width="500" v-model="insertFailDialog">
      <v-card class="px-8 pt-5 pb-6">
        <v-card-title class="red-6--text">操作失敗</v-card-title>
        <v-card-text>
          <div class="mt-2">
            <div class="pl-4">
              {{ insertFailMsg }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="justify-space-around">
          <v-btn color="primary" class="px-4" @click="insertFailDialog = false">我知道了</v-btn>
          <v-btn
            dark
            color="grey-5"
            class="px-4"
            @click="afterInsertFail(editAppointment, 'allow')"
          >手動確認可預約
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SendLineMessageV2
      ref="SendLineMessageV2"
      :btnProps="{text: true, small: true}"
      :patient="{
        patientName: editAppointment?.name,
        patientCode: editAppointment?.patient_code,
        patientPhone: editAppointment?.phone,
      }"
      style="display: none;"
    />
    <SendSmsMessageV2
      v-show="false"
      ref="smsMessageV2"
      :btnProps="{text: true}"
      :patient="{
        patientName: editAppointment?.name,
        patientCode: editAppointment?.patient_code,
        patientPhone: editAppointment?.phone,
      }"
    />
    <SendFailDialog
      v-model="showSendFailDialog"
      :message="dialogOptions.message"
      :buttons="dialogOptions.buttons"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  getAppointmentAPI,
  getChannelQuotaAPI,
  getClinicListAPI,
  getDoctorListAPI,
  getMessageSettingAPI,
  getPatientChannelBindStatusAPI,
  getSMSQuotaChannelInfoAPI,
  updateAppointmentAPI,
} from "@/api";
import SendLineMessageV2 from "@/components/utils/SendLineMessageV2.vue";
import SendSmsMessageV2 from "@/components/utils/SendSmsMessageV2.vue";
import SendFailDialog from "@/components/utils/SendFailDialog.vue";

export default {
  name: "ReserveTable",
  components: {SendFailDialog, SendSmsMessageV2, SendLineMessageV2},
  props: ['status', 'completed'],
  data() {
    return {
      appointments: [],
      totalAppointments: 0,
      limit: 10,
      page: 1,
      pageLoading: false,
      crmNoteDialog: false,
      editAppointment: null,
      confirmDialog: false,
      messageDialog: false,
      confirmTitle: null,
      confirmText: null,
      actionType: null,
      messageTitle: null,
      messageText: null,
      rejectReasonDialog: false,
      rejectItem: null,
      rejectReasonIndex: null,
      getDataTimer: null,
      insertFailDialog: false,
      insertFailMsg: null,

      showSendFailDialog: false,
      dialogOptions: {
        message: '',
        buttons: []
      },
      sendLineSuffix: '',
      sendSmsSuffix: '',

      // 篩選條件
      filters: {
        searchText: '',
        doctorCode: null,
        clinicCode: null,
        createdDateRange: [],
        bookDateRange: [],
        completedList: [],
      },
      doctorList: [],
      clinicList: [],
      createdDateMenuOpen: false,
      bookDateMenuOpen: false,
      isFiltering: false,
      filterPanelExpanded: false,
    }
  },
  computed: {
    ...mapGetters([
      'axiosParamData',
      'clinicUser',
      'doctorUser',
      'ownChannelLoaded',
      'hasOwnChannel'
    ]),
    headers() {
      const headers = [
        {text: this.$t('ReserveTable.patientName'), align: 'center', value: 'name', sortable: false, width: 90},
        {text: this.$t('ReserveTable.patientPhone'), align: 'center', value: 'phone', sortable: false, width: 110},
        {text: this.$t('ReserveTable.patientBirth'), align: 'center', value: 'birthday', sortable: false, width: 120},
        {text: this.$t('ReserveTable.patientGender'), align: 'center', value: 'sex', sortable: false, width: 70},
        {
          text: this.$t('ReserveTable.reserveDoctor'),
          align: 'center',
          value: 'doctor_name',
          sortable: false,
          width: 90
        },
        {text: this.$t('ReserveTable.addOnItem'), align: 'center', value: 'add_on_item', sortable: false, width: 90},
        {text: this.$t('ReserveTable.bookTime'), align: 'center', value: 'prefer_time', sortable: false, width: 160},
        {text: this.$t('ReserveTable.treatment'), align: 'center', value: 'item', sortable: false, width: 90},
        {text: this.$t('ReserveTable.note'), align: 'center', value: 'note', sortable: false, width: 180},
        {text: this.$t('ReserveTable.source'), align: 'center', value: 'source', sortable: false, width: 130},
        {text: this.$t('ReserveTable.createTime'), align: 'center', value: 'create_time', sortable: false, width: 110},
        {text: this.$t('ReserveTable.crmNote'), align: 'center', value: 'crm_note', sortable: false, width: 180},
        {text: this.$t('ReserveTable.action'), align: 'center', value: 'action', sortable: false, width: 180},
        {text: '傳送訊息', align: 'center', value: 'send', sortable: false, width: 110},
      ]
      if (this.doctorUser)
        headers[4] = {
          text: this.$t('ReserveTable.reserveClinic'),
          align: 'center',
          value: 'clinic_name',
          sortable: false,
          width: 90
        }
      if (this.status) {
        headers.insert(11, {
          text: this.$t('ReserveTable.updateTime'),
          align: 'center',
          value: 'update_time',
          sortable: false,
          width: 110
        })
        headers.insert(12, {
          text: this.$t('ReserveTable.showResult'),
          align: 'center',
          value: 'complete_status',
          sortable: false,
          width: 150
        })
      }

      return headers
    },
    createdDateRangeText() {
      if (!this.filters.createdDateRange || this.filters.createdDateRange.length === 0) {
        return ''
      }
      const sorted = this.filters.createdDateRange.slice().sort((a, b) => new Date(a) - new Date(b))
      return sorted.join(' ~ ')
    },
    bookDateRangeText() {
      if (!this.filters.bookDateRange || this.filters.bookDateRange.length === 0) {
        return ''
      }
      const sorted = this.filters.bookDateRange.slice().sort((a, b) => new Date(a) - new Date(b))
      return sorted.join(' ~ ')
    },
    completedOptions() {
      return [
        { text: '此時段可預約', value: '1' },
        { text: '已額外安排時間', value: '2' },
        { text: '此時段無法預約', value: '3' },
      ]
    },
    activeFilterCount() {
      let count = 0
      if (this.filters.searchText) count++
      if (this.filters.doctorCode || this.filters.clinicCode) count++
      if (this.filters.createdDateRange && this.filters.createdDateRange.length >= 1) count++
      if (this.filters.bookDateRange && this.filters.bookDateRange.length >= 1) count++
      if (this.filters.completedList && this.filters.completedList.length > 0) count++
      return count
    },
    tableHeight() {
      // 頂部高度估算：navbar(64) + tabs(48) + 篩選面板標題(52) + 篩選內容(展開時約120) + padding/margin(40) + footer(60)
      const baseHeight = 264
      const filterContentHeight = this.filterPanelExpanded ? 120 : 0
      const totalTopHeight = baseHeight + filterContentHeight

      const calculated = window.innerHeight - totalTopHeight
      // 最小高度 400px，確保小螢幕也能看到幾筆資料
      return Math.max(calculated, 400)
    },
  },
  watch: {
    page() {
      this.getDataFromApi()
    },
    limit() {
      this.getDataFromApi()
    },
  },
  mounted() {
    if (this.clinicUser) {
      this.fetchDoctorList()
    } else {
      this.fetchClinicList()
    }
    this.getDataFromApi()
  },
  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    ...mapActions({
      getUnreadNotice: 'getUnreadNotice',
    }),
    getDataFromApi() {
      clearTimeout(this.getDataTimer)
      this.pageLoading = true

      // 解析搜尋文字
      const parsed = this.parseSearchText(this.filters.searchText)

      // 處理建立日期範圍（選一個日期時起始與結束同天）
      let createdAtStart = null
      let createdAtEnd = null
      if (this.filters.createdDateRange && this.filters.createdDateRange.length >= 1) {
        const sorted = this.filters.createdDateRange.slice().sort((a, b) => new Date(a) - new Date(b))
        createdAtStart = `${sorted[0]} 00:00:00`
        createdAtEnd = `${sorted[sorted.length - 1]} 23:59:59`
      }

      // 處理偏好日期範圍（選一個日期時起始與結束同天）
      let bookTimeStart = null
      let bookTimeEnd = null
      if (this.filters.bookDateRange && this.filters.bookDateRange.length >= 1) {
        const sorted = this.filters.bookDateRange.slice().sort((a, b) => new Date(a) - new Date(b))
        bookTimeStart = `${sorted[0]} 00:00:00`
        bookTimeEnd = `${sorted[sorted.length - 1]} 23:59:59`
      }

      const params = {
        ...this.axiosParamData,
        limit: this.limit,
        page: this.page,
        status: this.status
      }

      // 加入篩選參數（只有在篩選模式下才加入）
      if (this.isFiltering) {
        if (parsed.name) params.name = parsed.name
        if (parsed.phone) params.phone = parsed.phone
        if (parsed.birthday) params.birthday = parsed.birthday
        if (this.clinicUser && this.filters.doctorCode) params.filter_doctor_code = this.filters.doctorCode
        if (!this.clinicUser && this.filters.clinicCode) params.filter_clinic_code = this.filters.clinicCode
        if (createdAtStart) params.created_at_start = createdAtStart
        if (createdAtEnd) params.created_at_end = createdAtEnd
        if (bookTimeStart) params.book_time_start = bookTimeStart
        if (bookTimeEnd) params.book_time_end = bookTimeEnd
        if (this.filters.completedList && this.filters.completedList.length > 0) {
          params.completed = this.filters.completedList.join(',')
        }
      }

      if (this.completed && !this.isFiltering) params.completed = this.completed
      getAppointmentAPI(params).then(res => {
        this.totalAppointments = parseInt(res.data.paginate.total)
        this.appointments = res.data.data.map(v => {
          let source
          if (v.is_np === '0') {
            source = `NP/${v.channel_name}`;
            source += (v.inviter_alias !== '') ? `/${v.inviter_alias}` : ``;
          } else {
            source = `${this.$t('ReserveTable.oldPatient')}/${v.channel_name}`
          }
          if (v.doctor_name === '') {
            v.doctor_name = '不指定'
          }
          return {
            ...v,
            source
          }
        })
        this.getDataTimer = setTimeout(() => {
          this.getDataFromApi()
        }, 1000 * 60 * 5)
        this.pageLoading = false
      }).catch(e => {
        alert(this.$t('GENERAL.oops'))
        console.error(e)
      })
    },
    editCrmNote(obj) {
      this.editAppointment = JSON.parse(JSON.stringify(obj))
      this.crmNoteDialog = true
    },
    update() {
      const data = {
        ...this.axiosParamData,
        crm_note: this.editAppointment.crm_note
      }
      updateAppointmentAPI(this.editAppointment.id, data).then(() => {
        this.editAppointment = null
        this.crmNoteDialog = false
        this.getDataFromApi()
      }).catch(e => {
        alert(this.$t('GENERAL.oops'))
        console.error(e)
      })
    },
    showPreferTime(appointment) {
      const weekText = [
        'ReserveTable.Sun', 'ReserveTable.Mon',
        'ReserveTable.Tues', 'ReserveTable.Wed',
        'ReserveTable.Thur', 'ReserveTable.Fri',
        'ReserveTable.Sat'
      ]
      if (appointment.book_time) {
        return `${this.$moment(appointment.book_time).format('YYYY/MM/DD')}
        (${this.$tc(weekText[this.$moment(appointment.book_time).day()], 1)})
        ${this.$moment(appointment.book_time).format('HH:mm')}`
      } else if (appointment.week) {
        const timeSlot = [this.$t('ReserveTable.morning'), this.$t('ReserveTable.afternoon'), this.$t('ReserveTable.night')]
        let _ = this.$tc(weekText[parseInt(appointment.week)], 2)
        if (appointment.time_slot) return `${_} ${timeSlot[parseInt(appointment.time_slot)]}`
        return _
      }
      return this.$t('ReserveTable.notSpecified')
    },
    handleChangeStatus(item, action, rejectReasonIndex = 3) {
      this.editAppointment = JSON.parse(JSON.stringify(item))
      this.actionType = action
      switch (action) {
        case 'allow':
          this.rejectItem = null;
          this.rejectReasonIndex = null;
          this.confirmTitle = this.$t('ReserveTable.sendAndComplete');
          this.confirmText = item.remind_1;
          break;

        case 'allowInsert':
          this.rejectItem = null;
          this.rejectReasonIndex = null;
          this.confirmTitle = this.$t('ReserveTable.sendAndComplete');
          if (item.system_code === 'manual') {
            this.confirmText = `<div>${item.remind_1}</div><div class="mt-2 red-6--text">請留意！此筆約診將直接匯入約診表！</div>`;
          } else {
            this.confirmText = `<div>${item.remind_1}</div><div class="mt-2 red-6--text">請留意！此筆約診將直接匯入${item.system_name}！</div>`;
          }

          break;

        case 'reject':
          this.rejectReasonIndex = rejectReasonIndex;
          this.rejectItem = item;
          this.confirmTitle = this.$t('ReserveTable.sendAndComplete');
          this.confirmText = item.remind_3[rejectReasonIndex].msg;
          this.rejectReasonDialog = false;
          break;

        case 'extra':
          this.rejectItem = null;
          this.rejectReasonIndex = null;
          this.confirmTitle = this.$t('ReserveTable.completeWithoutSend');
          this.confirmText = null;
          break;

        case 'know':
          this.rejectItem = null;
          this.rejectReasonIndex = null;
          this.confirmTitle = '此筆線上預約將移動至已聯繫，系統將不另外傳送訊息通知，請致電與病人確認其他時間。';
          this.confirmText = null;
          break;

        default:
          this.rejectItem = null;
          this.rejectReasonIndex = null;
          this.confirmTitle = this.$t('ReserveTable.remind');
          this.confirmText = null;
          break;
      }

      this.confirmDialog = true
    },
    changeStatus(event, withoutInfo = false) {
      let vm = this
      let completed, appointment_status, insert_to_his
      let completed_reason = null
      switch (this.actionType) {
        case 'allow':
          completed = 1;
          this.messageTitle = this.$t('ReserveTable.moveToComplete');
          this.messageText = this.$t('ReserveTable.pleaseCreateReserve');
          break;

        case 'allowInsert':
          completed = 1;
          insert_to_his = 1;
          this.messageTitle = this.$t('ReserveTable.moveToComplete');
          if (this.editAppointment.system_code === 'manual') {
            this.messageText = '此筆約診已移動至已聯繫，且已寫入約診表';
          } else {
            this.messageText = '此筆約診已移動至已聯繫，且已匯入健保系統';
          }
          break;

        case 'reject':
          completed = 3;
          completed_reason = this.rejectItem.remind_3[this.rejectReasonIndex].reason;
          this.messageTitle = this.$t('ReserveTable.pleaseCall');
          this.messageText = null;
          break;

        case 'extra':
          completed = 2;
          this.messageTitle = this.$t('ReserveTable.moveToComplete');
          this.messageText = null;
          break;

        case 'concat':
          completed = 1;
          this.messageTitle = this.$t('ReserveTable.moveToComplete');
          this.messageText = this.$t('ReserveTable.pleaseCreateReserve');
          break;

        case 'know':
          appointment_status = 1;
          this.messageTitle = null;
          this.messageText = null;
          break;

        default:
          if (Number(this.editAppointment.completed) === 4) {
            appointment_status = 0;
            this.messageTitle = null;
            this.messageText = null;
          } else {
            completed = 0;
            this.messageTitle = null;
            this.messageText = null;
          }
          break;
      }

      const data = {
        ...this.axiosParamData,
        completed,
        completed_reason
      }

      if (completed === 1 && insert_to_his) data.insert_to_his = insert_to_his;
      if (appointment_status !== undefined) data.appointment_status = appointment_status;
      if (withoutInfo) data.skip_info = 1;
      // if (completed !== undefined) data.completed = completed
      this.confirmDialog = false
      this.pageLoading = true

      updateAppointmentAPI(this.editAppointment.id, data).then(() => {
        if (completed) this.messageDialog = true

        // reset variable
        this.actionType = null

        this.getDataFromApi()
        this.getUnreadNotice()

        this.pageLoading = false
      }).catch(e => {
        const failCode = e.response?.data?.fail_code;

        switch (failCode) {
          case 'LINE_NO_BALANCE':
            this.dialogOptions = {
              message: 'LINE 訊息額度不足',
              buttons: [
                {
                  text: '升級方案',
                  color: 'red-6',
                  href: this.$enum.LINE_OFFICE_WEBSITE,
                  options: {dark: true, target: '_blank'}
                },
                {text: '聯繫客服', color: 'primary', href: this.$enum.DENTCO_CS_LINE, options: {target: '_blank'}},
                {
                  text: '我會自行致電通知患者',
                  color: 'orange-4',
                  options: {dark: true},
                  onClick: () => {
                    this.showSendFailDialog = false;
                    this.changeStatus(null, true);
                  }
                }
              ]
            }
            this.showSendFailDialog = true
            break

          case 'LINE_ERROR':
            this.dialogOptions = {
              message: 'LINE 系統發生異常，請稍後再試',
              buttons: [
                {
                  text: '我知道了，關閉視窗', color: 'grey', onClick: () => {
                    this.showSendFailDialog = false
                  }, options: {dark: true}
                },
                {text: '聯繫客服', color: 'primary', href: this.$enum.DENTCO_CS_LINE, options: {target: '_blank'}},
                {
                  text: '我會自行致電通知患者',
                  color: 'orange-4',
                  options: {dark: true},
                  onClick: () => {
                    this.showSendFailDialog = false;
                    this.changeStatus(null, true);
                  }
                }
              ]
            }
            this.showSendFailDialog = true
            break

          case 'SMS_NO_BALANCE':
            this.dialogOptions = {
              message: '簡訊額度不足',
              buttons: [
                {text: '聯繫客服', color: 'primary', href: this.$enum.DENTCO_CS_LINE, options: {target: '_blank'}},
                {
                  text: '我會自行致電通知患者',
                  color: 'orange-4',
                  options: {dark: true},
                  onClick: () => {
                    this.showSendFailDialog = false;
                    this.changeStatus(null, true);
                  }
                }
              ]
            }
            this.showSendFailDialog = true
            break

          case 'SMS_ERROR':
            this.dialogOptions = {
              message: '簡訊系統發生異常，請稍後再試',
              buttons: [
                {
                  text: '我知道了，關閉視窗', color: 'grey', onClick: () => {
                    this.showSendFailDialog = false
                  }, options: {dark: true}
                },
                {text: '聯繫客服', color: 'primary', href: this.$enum.DENTCO_CS_LINE, options: {target: '_blank'}},
                {
                  text: '我會自行致電通知患者',
                  color: 'orange-4',
                  options: {dark: true},
                  onClick: () => {
                    this.showSendFailDialog = false;
                    this.changeStatus(null, true);
                  }
                }
              ]
            }
            this.showSendFailDialog = true
            break

          case '500':
            this.insertFailMsg = e.response?.data?.messages || '無任何訊息'
            this.insertFailDialog = true
            break

          default:
            // 可選擇性處理未知錯誤
            console.error('Unhandled fail_code:', failCode)
            if (e.response?.data?.messages) {
              alert(e.response?.data?.messages)
            } else {
              alert(this.$t('GENERAL.oops'))
              console.error(e)
            }
            break
        }
        this.pageLoading = false
      })
    },
    setCancelRed(item) {
      if (Number(item.appointment_status) === 0 && Number(item.completed) === 4)
        return 'red--text'
    },
    showInsertSystemBtn(item) {
      return (
        ['leyan', 'manual'].includes(item.system_code) &&
        item.insert_his_reserve === '1'
      );
    },
    async getSendChannelInfo(event, on) {
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData)
        this.setOwnChannel(res.data.own_channel)
      }

      const [bindStatusRes, smsQuotaRes] = await Promise.all([
        getPatientChannelBindStatusAPI(this.editAppointment.patient_code, this.axiosParamData),
        getSMSQuotaChannelInfoAPI(this.axiosParamData).catch(err => err.response)
      ])

      const bindData = bindStatusRes.data
      const smsQuota = smsQuotaRes.data?.quota
      this.sendSmsSuffix = ` - 剩餘${smsQuota}點`

      let lineBindStatus = false

      if (this.hasOwnChannel) {
        lineBindStatus = bindData?.own_channel_bind_status === 1

        if (lineBindStatus) {
          const res3 = await getChannelQuotaAPI(this.axiosParamData)
          const lineQuota = Number(res3.data.quota) - Number(res3.data.total_usage)

          this.sendLineSuffix = lineQuota > 0 ? ' - 可發送' : ' - 額度不足'
        } else {
          this.sendLineSuffix = ' - 未綁定'
        }
      } else {
        lineBindStatus = bindData?.dentco_channel_bind_status === 1
        this.sendLineSuffix = lineBindStatus ? ' - 可發送' : ' - 未綁定'
      }

      await on.click(event)
    },
    afterInsertFail() {
      this.actionType = 'allow'
      this.changeStatus()
      this.insertFailDialog = false
    },
    fetchDoctorList() {
      getDoctorListAPI(this.axiosParamData).then(res => {
        this.doctorList = res.data || []
      }).catch(e => {
        console.error('Failed to fetch doctor list:', e)
      })
    },
    fetchClinicList() {
      getClinicListAPI(this.axiosParamData).then(res => {
        this.clinicList = res.data || []
      }).catch(e => {
        console.error('Failed to fetch clinic list:', e)
      })
    },
    parseSearchText(text) {
      const result = { name: null, phone: null, birthday: null }
      if (!text || !text.trim()) return result

      const trimmed = text.trim()

      // 判斷是否為手機號碼（以 09 開頭或純數字且長度 10）
      if (/^09\d{8}$/.test(trimmed)) {
        result.phone = trimmed
        return result
      }

      // 判斷是否為生日（民國格式，6 或 7 碼純數字）
      if (/^\d{6,7}$/.test(trimmed)) {
        result.birthday = trimmed
        return result
      }

      // 判斷是否為部分手機號碼（以 09 開頭但不完整，或純數字）
      if (/^09\d*$/.test(trimmed) || /^\d{4,}$/.test(trimmed)) {
        result.phone = trimmed
        return result
      }

      // 其他情況視為姓名
      result.name = trimmed
      return result
    },
    applyFilter() {
      this.isFiltering = true
      this.page = 1
      this.getDataFromApi()
    },
    clearFilter() {
      this.filters = {
        searchText: '',
        doctorCode: null,
        clinicCode: null,
        createdDateRange: [],
        bookDateRange: [],
        completedList: [],
      }
      this.isFiltering = false
      this.page = 1
      this.getDataFromApi()
    }
  },

  beforeDestroy() {
    clearTimeout(this.getDataTimer)
  },
}
</script>

<style scoped>
.filter-header {
  background-color: #fafafa;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: #f0f0f0;
}

::v-deep .v-data-table-header th {
  background-color: #f5f5f5;
  font-weight: 600;
  white-space: nowrap;
}

/* 手機版：篩選欄位佔滿寬度 */
@media (max-width: 600px) {
  .filter-content {
    flex-direction: column;
    align-items: stretch !important;
  }

  .filter-content .v-text-field,
  .filter-content .v-select,
  .filter-content .v-input {
    max-width: 100% !important;
    min-width: 100% !important;
  }

  .filter-content .v-btn {
    width: 100%;
  }
}
</style>
